import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  r: number;
  vx: number;
  vy: number;
  life: number;
  lifeSpeed: number;
  hue: number;
}

interface Orb {
  x: number;
  y: number;
  r: number;
  hue: number;
  phase: number;
  speed: number;
}

/**
 * Амбиентный «живой» canvas-фон (оранжевая аврора + всплывающие угольки).
 * Общий для иммерсивных полноэкранных потоков — процесса заучивания (сессия)
 * и режима ознакомления. Позиционируется CSS-классами `.aurora`/`.atm-canvas`,
 * которые объявлены per-flow (`.lx-sess-wrap .aurora`, `.lx-prev-wrap .aurora`).
 */
export function Aurora() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cnv = canvasRef.current;
    if (!cnv) return;
    const ctx = cnv.getContext('2d');
    if (!ctx) return;

    let w = 0;
    let h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      w = cnv.clientWidth;
      h = cnv.clientHeight;
      cnv.width = w * dpr;
      cnv.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(cnv);

    // prefers-reduced-motion: не запускаем непрерывную canvas-анимацию. CSS-градиент
    // wrapper'а остаётся фоном — экран не пустой, просто без «живого» движения.
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (prefersReduced) {
      return () => ro.disconnect();
    }

    const N = 38;
    const parts: Particle[] = [];
    for (let i = 0; i < N; i++) {
      parts.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.6 + Math.random() * 2.2,
        vx: (Math.random() - 0.5) * 0.18,
        vy: -0.1 - Math.random() * 0.28,
        life: Math.random(),
        lifeSpeed: 0.0008 + Math.random() * 0.0014,
        hue: 22 + Math.random() * 18,
      });
    }

    const orbs: Orb[] = [
      { x: 0.18, y: 0.22, r: 280, hue: 24, phase: 0, speed: 0.00018 },
      { x: 0.78, y: 0.3, r: 260, hue: 18, phase: 1.7, speed: 0.00014 },
      { x: 0.3, y: 0.82, r: 320, hue: 28, phase: 3.1, speed: 0.00011 },
      { x: 0.72, y: 0.78, r: 240, hue: 14, phase: 5.0, speed: 0.00016 },
    ];

    let raf = 0;
    let t0 = performance.now();
    let running = true;
    // Кап ~60 FPS: на 120/165-Гц экранах tick звался 120–165 раз/сек, и каждый кадр
    // аллоцировал ~80 радиальных градиентов — лишняя нагрузка на CPU/GPU/батарею.
    // 60 FPS для амбиентного фона визуально неотличимо.
    const frameInterval = 1000 / 60;
    let lastRender = 0;

    const tick = (now: number) => {
      if (!running) return;
      raf = requestAnimationFrame(tick);
      if (lastRender && now - lastRender < frameInterval) return;
      lastRender = now;
      const t = now - t0;

      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(14,9,7,1)';
      ctx.fillRect(0, 0, w, h);

      ctx.globalCompositeOperation = 'lighter';
      for (const o of orbs) {
        const px = (o.x + Math.sin(t * o.speed + o.phase) * 0.1) * w;
        const py = (o.y + Math.cos(t * o.speed * 0.8 + o.phase) * 0.08) * h;
        const rr = o.r * (0.92 + Math.sin(t * 0.0006 + o.phase) * 0.1);
        const g = ctx.createRadialGradient(px, py, 0, px, py, rr);
        g.addColorStop(0, `hsla(${o.hue}, 80%, 55%, 0.30)`);
        g.addColorStop(0.4, `hsla(${o.hue}, 75%, 45%, 0.12)`);
        g.addColorStop(1, `hsla(${o.hue}, 70%, 30%, 0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(px, py, rr, 0, Math.PI * 2);
        ctx.fill();
      }

      // (1 - cos(ωt)) / 2 — зеркальный ping-pong sweep без teleport-jump'а.
      const sweep = (1 - Math.cos(t * 0.00006 * Math.PI)) / 2;
      const sx = -0.3 * w + sweep * 1.6 * w;
      const grad = ctx.createLinearGradient(sx, 0, sx + w * 0.35, h);
      grad.addColorStop(0, 'rgba(232,140,60,0)');
      grad.addColorStop(0.5, 'rgba(232,140,60,0.06)');
      grad.addColorStop(1, 'rgba(232,140,60,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      for (const p of parts) {
        p.x += p.vx;
        p.y += p.vy;
        p.life += p.lifeSpeed;
        if (p.life >= 1 || p.y < -10 || p.x < -10 || p.x > w + 10) {
          p.x = Math.random() * w;
          p.y = Math.random() * h;
          p.life = 0;
          p.vx = (Math.random() - 0.5) * 0.18;
          p.vy = -0.1 - Math.random() * 0.28;
        }
        const lifeAlpha = Math.sin(p.life * Math.PI);
        const alpha = 0.55 * lifeAlpha;
        const r = p.r;
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 5);
        g.addColorStop(0, `hsla(${p.hue}, 95%, 75%, ${alpha})`);
        g.addColorStop(0.4, `hsla(${p.hue}, 95%, 65%, ${alpha * 0.35})`);
        g.addColorStop(1, `hsla(${p.hue}, 95%, 60%, 0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `hsla(${p.hue}, 100%, 88%, ${alpha * 1.2})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalCompositeOperation = 'source-over';
      const v = ctx.createRadialGradient(
        w * 0.5,
        h * 1.1,
        h * 0.2,
        w * 0.5,
        h * 0.5,
        Math.max(w, h) * 0.8,
      );
      v.addColorStop(0, 'rgba(0,0,0,0)');
      v.addColorStop(1, 'rgba(8,4,2,0.55)');
      ctx.fillStyle = v;
      ctx.fillRect(0, 0, w, h);
    };
    raf = requestAnimationFrame(tick);

    const onVis = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        t0 = performance.now();
        raf = requestAnimationFrame(tick);
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return (
    <div className="aurora">
      <canvas ref={canvasRef} className="atm-canvas" />
      <div className="grain" />
    </div>
  );
}
